import { currencyFormatter } from "@common/helpers/currency";
import { pluralizeWithCount } from "@common/helpers/string";
import { SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE } from "@api/scheduleMilestone.api";

export function formatMilestoneRecognizedToast(options: {
    name: string;
    amount: number;
    isOverRecognized: boolean;
}): string {
    const base = `Recognized ${currencyFormatter.format(options.amount)} for ${options.name}`;
    return options.isOverRecognized
        ? `${base}. ${formatOverRecognizedSuffix(1)}`
        : base;
}

export function formatOverRecognizedSuffix(count: number): string {
    return count === 1
        ? "This exceeds the schedule's remaining balance."
        : formatOverRecognizedCountSuffix(count);
}

export function formatMilestoneAlreadyRecognizedToast(name: string): string {
    return `"${name}" is already recognized`;
}

export const MILESTONE_ACHIEVED_BULK_DISABLED_REASON =
    "Select pending milestone rows";
export const MILESTONE_ACHIEVED_BULK_LIMIT_REASON = `Select up to ${SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT} milestones to recognize them`;

export function formatMilestoneAchievedBulkTitle(count: number): string {
    return `Milestone achieved · ${pluralizeWithCount("milestone", count)}`;
}

export const MILESTONE_ACHIEVED_BATCH_DATE_LABEL = "Use one date for all";

function isntOrArent(count: number): string {
    return count === 1 ? "isn't" : "aren't";
}

/** Shown, never blocking, when a date to post is after today. */
export function formatMilestonesNotDueYetWarning(count: number): string {
    return `${pluralizeWithCount("milestone", count)} ${isntOrArent(count)} due yet`;
}

export function formatMilestoneAchievedBulkBody(skippedCount: number): string {
    const base =
        "Kick posts one recognition entry per milestone on its posting date. Kick fixed each amount when you applied the policy.";
    if (skippedCount === 0) {
        return base;
    }
    return `${base} ${pluralizeWithCount("selected row", skippedCount)} ${isntOrArent(skippedCount)} a pending milestone and will be skipped.`;
}

/**
 * `Recognized 3 milestones`, then `, 1 failed` and `, 2 rows skipped` only
 * when there is something to say; the overrun note follows as a sentence.
 */
export function formatMilestonesRecognizedBulkToast(options: {
    recognizedCount: number;
    failedCount: number;
    skippedCount: number;
    overRecognizedCount: number;
}): string {
    const parts = [
        `Recognized ${pluralizeWithCount("milestone", options.recognizedCount)}`,
        ...(options.failedCount > 0 ? [`${options.failedCount} failed`] : []),
        ...(options.skippedCount > 0
            ? [`${pluralizeWithCount("row", options.skippedCount)} skipped`]
            : []),
    ];
    const summary = parts.join(", ");
    return options.overRecognizedCount > 0
        ? `${summary}. ${formatOverRecognizedCountSuffix(options.overRecognizedCount)}`
        : summary;
}

export function formatOverRecognizedCountSuffix(count: number): string {
    return `${pluralizeWithCount("milestone", count)} ${count === 1 ? "exceeds" : "exceed"} the schedule's remaining balance.`;
}

export function formatMilestonesRecognizedBulkFailureToast(
    firstFailureMessage: string | undefined,
): string {
    return firstFailureMessage
        ? `${SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE}. ${firstFailureMessage}`
        : SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE;
}
